import { Pause, PlayArrow } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import React from "react";
import { getAudioDurationSeconds } from "lib/audio";

type AudioPlayerProps = {
  file?: Blob | null
  initialDuration?: number  // display this if file is absent
  onDurationUpdated?: (duration: number) => any
  initialEnvelope?: number[]
  envelopeResolution?: number
  colorEnvelope?: string
  colorProgress?: string
}

/**
 * Audio player widget displaying a waveform envelope.
 *
 * @param file - (Blob) an audio file to play
 * @param initialDuration - display this duration when there's no file present
 * @param onDurationUpdated - called when file duration is determined. Define with useCallback to avoid unnecessary rerenders.
 * @param initialEnvelope - display this envelope when there's no file present
 * @param envelopeResolution - limit waveform envelope plot to this many samples, to optimize rendering long files.
 * @param colorEnvelope - fill color for the unplayed part of the envelope
 * @param colorProgress - fill color for the played part of the envelope
 */
export const VisualAudioPlayer: React.FC<AudioPlayerProps> = ({
  file,
  initialDuration = 0,
  onDurationUpdated,
  initialEnvelope,
  envelopeResolution = 200,
  colorEnvelope = `rgba(${0x6b},${0x72},${0x80},0.5)`,
  colorProgress = '#1976d2',
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const [audioElement, setAudioElement] = React.useState<HTMLAudioElement | null>(null)
  const [duration, setDuration] = React.useState<number>(0)
  const [envelope, setEnvelope] = React.useState<number[]>([])
  const [playing, setPlaying] = React.useState<boolean>(false)
  const [redrawSentinel, setRedrawSentinel] = React.useState<{}>({})

  // stop on unmount
  React.useEffect(() => {
    return () => {
      setAudioElement(a => {
        if (a) { a.pause() }
        return null
      })
      setPlaying(false)
    }
  }, [])

  // use initial envelope when we don't have a file
  React.useEffect(() => {
    if (!file && initialEnvelope) { setEnvelope(initialEnvelope) }
  }, [file, initialEnvelope])

  // update state when file changes
  React.useEffect(() => {
    if (!file) { return }
    // create audio element to play back
    const audio = new Audio(URL.createObjectURL(file))
    // set displayed duration from it
    getAudioDurationSeconds(audio).then(setDuration)
    // rewind on end since we draw a minimal playback UI
    audio.addEventListener('ended', () => {
      setPlaying(false)
      audio.pause()
      audio.currentTime = 0
    })
    setAudioElement(audio)
    // also extract the envelope
    // we need an AudioContext to interpret audio files idk why
    const audioContext = new AudioContext()
    file.arrayBuffer()
      .then(b => audioContext.decodeAudioData(b))
      .then(b => {
        const envelope = Array.from({length: envelopeResolution}, () => 0)
        for (let channel = 0; channel < b.numberOfChannels; ++channel) {
          const channelData = b.getChannelData(channel)
          for (let i = 0; i < channelData.length; ++i) {
            const j = Math.floor(i / channelData.length * envelope.length)
            envelope[j] = Math.max(envelope[j], channelData[i])
          }
        }
        setEnvelope(envelope.map(a => Math.sqrt(a)))
      })
    // clean up if file becomes unset
    return () => {
      setAudioElement(null)
      URL.revokeObjectURL(audio.src)
    }
  }, [file, envelopeResolution])

  // notify parent of duration changes if requested
  React.useEffect(() => {
    if (isFinite(duration)) { onDurationUpdated?.(duration) }
  }, [onDurationUpdated, duration])

  // clock display
  const displayDuration = Math.round((file ? duration : initialDuration) ?? 0)
  const formattedDuration = `${Math.floor(displayDuration / 60)}:${("0" + (displayDuration % 60).toString()).slice(-2)}`

  // subroutine to redraw waveform and cursor
  const redrawCanvas = React.useCallback((envelope?: number[]) => {
    if (!canvasRef.current) { return }
    // clear the canvas
    const w = canvasRef.current.width = canvasRef.current.clientWidth
    const h = canvasRef.current.height = canvasRef.current.clientHeight
    // acquire drawing context
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) { return }
    ctx.save()
    // choose coordinates: x goes right from 0 to 1, and y goes up from -1 to 1
    ctx.scale(w, -h/2)
    ctx.translate(0, -1)
    // draw envelope if we have it
    const rawProgress = audioElement ? audioElement.currentTime / audioElement.duration : 1
    const progress = isFinite(rawProgress) ? rawProgress : 0
    if (envelope) {
      const sampledEnvelope = Array.from({length: envelopeResolution}, () => 0)
      for (let i = 0; i < envelope.length; ++i) {
        const j = Math.floor((i / envelope.length) * sampledEnvelope.length)
        sampledEnvelope[j] = Math.max(sampledEnvelope[j], envelope[i])
      }
      const progressScaled = Math.max(0, progress * sampledEnvelope.length - 0.5)
      const left = Math.floor(progressScaled)
      const right = Math.ceil(progressScaled)
      const frac = progressScaled - left
      const progressY = sampledEnvelope[left] * (1 - frac) + sampledEnvelope[right] * frac
      // played half
      ctx.fillStyle = colorProgress
      ctx.beginPath()
      ctx.moveTo(0, 0)
      let i;
      for (i = 0; i < left; ++i) { ctx.lineTo((i + 0.5)/sampledEnvelope.length, sampledEnvelope[i]) }
      ctx.lineTo(progress, progressY)
      ctx.lineTo(progress, -progressY)
      for (; i >= 0; --i) { ctx.lineTo((i + 0.5)/sampledEnvelope.length, -sampledEnvelope[i]) }
      ctx.closePath()
      ctx.fill()
      // unplayed half
      ctx.fillStyle = colorEnvelope
      ctx.beginPath()
      ctx.moveTo(progress, progressY)
      for (i = right; i < sampledEnvelope.length; ++i) { ctx.lineTo((i + 0.5)/sampledEnvelope.length, sampledEnvelope[i]) }
      ctx.lineTo(1, 0)
      for (; i >= right; --i) { ctx.lineTo((i + 0.5)/sampledEnvelope.length, -sampledEnvelope[i]) }
      ctx.lineTo(progress, -progressY)
      ctx.closePath()
      ctx.fill()
    } else {  // fall back to a plain progress bar
      ctx.fillStyle = colorProgress
      ctx.fillRect(0, -0.2, progress, 0.4)
    }
    // restore state
    ctx.restore()
  }, [audioElement, envelopeResolution, colorEnvelope, colorProgress])

  // redraw if envelope changes, and continually while playing
  React.useEffect(() => {
    redrawCanvas(envelope)
    if (playing) {
      window.requestAnimationFrame(() => setRedrawSentinel({}))
    }
  }, [envelope, redrawCanvas, playing, redrawSentinel])

  // click to seek
  const handleClick: React.MouseEventHandler<HTMLCanvasElement> = e => {
    if (!canvasRef.current || !audioElement) { return }
    const frac = e.nativeEvent.offsetX / canvasRef.current.clientWidth
    const targetTime = duration * Math.min(Math.max(0, frac), 1)
    audioElement.currentTime = targetTime
    window.requestAnimationFrame(() => setRedrawSentinel({}))
  }

  return <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: '1',
    minWidth: '5em',
  }}>
    <IconButton
      title={playing ? 'Pause' : 'Play'}
      size="small"
      disabled={!audioElement}
      onClick={() => {
        if (!audioElement) { return }
        setPlaying(!playing)
        if (playing) {
          audioElement.pause()
        } else {
          audioElement.play()
        }
      }}
      sx={{
        color: 'text.secondary',
        '&:hover': {
          color: 'primary.main',
          backgroundColor: 'action.hover'
        }
      }}
    >
      {playing ? <Pause sx={{ fontSize: 20 }} /> : <PlayArrow sx={{ fontSize: 20 }} />}
    </IconButton>
    <canvas
      ref={canvasRef}
      style={{ height: '2em', minWidth: '2em', flexGrow: '1' }}
      onClick={handleClick}
    ></canvas>
    <Box sx={{ padding: '0em 0.25em' }}>{formattedDuration}</Box>
  </Box>
}
